import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getResultAnswers } from 'store/player/playerAsync';
import { selectResult } from 'store/player/playerSelectors';
import IQuiz from 'models/Quiz';
import QuizTypes from 'types/QuizTypes';
// MUI
import { Box } from '@mui/material';
// Components
import AppLoader from 'components/AppLoader';
import QRCode from './QRCode';
// i18next
import i18n from 'i18n'
import { teamSelectorFormations } from 'utilities/Utilities';
import PlayerSpot from 'components/PlayerSpot';
import IQuestion from 'models/Question';

let interval: any;

const LiveTeamboardPage:FC = () => {
  const { slug } = useParams<{ slug:string }>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.matchMedia("(min-width: 640px)").matches);

  const mediaHandler = (e:any) => setIsLargeScreen(e.matches);

  useEffect(() => {
    window.matchMedia("(min-width: 640px)").addEventListener('change', mediaHandler);

    return () => {
      window.matchMedia("(min-width: 640px)").removeEventListener('change', mediaHandler);
    }
  }, []);

  const result:any = useAppSelector(selectResult);
  const quiz:IQuiz | null = result?.quiz || null;

  const fetchResult = (isTimer: boolean) => {
    dispatch(getResultAnswers({ quizName: slug })).unwrap()
      .catch(() => {
        if (!isTimer) {
          navigate('/404-not-found');
        }
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    setLoading(true);
    fetchResult(false);

    interval = setInterval(() => { fetchResult(true) }, 10000);

    const rootDiv = document.getElementById('root');
    rootDiv?.classList.add('liveLeaderboardMode');

    return () => {
      clearInterval(interval);
      rootDiv?.classList.remove('liveLeaderboardMode');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!quiz) return;
    if (quiz.type !== QuizTypes.TeamSelector) navigate('/404-not-found');
    i18n.changeLanguage(quiz.language || 'en');
    document.documentElement.style.setProperty('--primaryColor', quiz.theme.primaryColor);
    // eslint-disable-next-line
  }, [quiz]);

  const bgImageUrl = quiz ? quiz.publicLeaderboard.wallpaperImage : '';
  const portraitBgImageUrl = quiz ? quiz.publicLeaderboard.portraitWallpaperImage : '';

  const answers = quiz?.questions.map((question: IQuestion) => {
    const resultItem = result.results.find((r: any) => r.questionRef === question.ref);
    const answersMap: Record<string, number> = resultItem?.answers || {};

    const entries = Object.entries(answersMap);
    if (!entries.length) return { title: '', percent: 0 };

    const [topRef, topVotes] = entries.reduce((max, curr) =>
      curr[1] > max[1] ? curr : max
    );

    const topAnswer = question.answers?.find(a => a.ref === topRef);
    const totalVotes = entries.reduce((sum, [, value]) => sum + value, 0);
    const percent = totalVotes ? Math.round((topVotes / totalVotes) * 100) : 0;

    return { title: topAnswer?.title || '', percent };
  });
  
  if (loading || !result || !quiz) return <AppLoader />;
  return (
    <Box
      className="
        flex flex-col h-full w-full
        overflow-hidden bg-cover bg-center
        text-white p-4 sm:p-8
      "
      sx={{
        color: quiz?.publicLeaderboard.textColor,
        '@media screen and (orientation: portrait)': {
          backgroundImage: `url(${portraitBgImageUrl || bgImageUrl})`,
        },
        '@media screen and (orientation: landscape)': {
          backgroundImage: `url(${bgImageUrl || portraitBgImageUrl})`,
        },
      }}
    >
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <img
          src={quiz.logoImage}
          alt={quiz.quizName}
          className="h-10 sm:h-16 md:h-20 object-contain shadow-primary rounded-lg"
          style={{ backgroundColor: quiz.logoImageBackgroundColor }}
        />
        {quiz.sponsorImage ? (
          <img
            src={quiz.sponsorImage}
            alt="sponsor"
            className="h-10 sm:h-16 md:h-20 object-contain shadow-primary rounded-lg"
            style={{ backgroundColor: quiz.sponsorImageBackgroundColor }}
          />
        ) : (
          <div className="w-10 sm:w-16 md:w-20" /> // баланс
        )}
      </div>

      {/* TITLE */}
      <div className="flex justify-center py-4 sm:py-8">
        <h1 className="text-xl sm:text-4xl md:text-6xl xl:text-8xl font-extrabold uppercase text-center border-b border-white inline-block px-4">
          {quiz.publicLeaderboard.headerText || quiz.quizName}
        </h1>
      </div>

      {/* MAIN SECTION */}
      <div
        className="
          flex-1 flex flex-col-reverse portrait:flex-col-reverse landscape:flex-row
          justify-between items-center gap-6 sm:gap-8
          overflow-hidden
        "
      >
        {/* LEFT: FIELD */}
        <div
          className="
            relative w-full landscape:w-2/3
            rounded-xl bg-cover bg-bottom shadow-lg overflow-hidden
            flex-1 min-h-[50vh] landscape:min-h-[70vh]
          "
          style={{
            backgroundImage: 'url(/images/teamboard.jpeg)',
          }}
        >
          <div className="absolute inset-0">
            {teamSelectorFormations[
              quiz.teamSelectorFormation as keyof typeof teamSelectorFormations
            ].map((pos, index) => (
              <PlayerSpot
                key={pos.role}
                size={isLargeScreen ? 'large' : 'small'}
                color={quiz.publicLeaderboard.primaryColor}
                positions={{ x: pos.x, y: pos.y }}
                answer={
                  answers
                    ? answers[index] || { title: '', percent: 0 }
                    : { title: '', percent: 0 }
                }
              />
            ))}
          </div>
        </div>

        {/* RIGHT: QR */}
        <div className="flex justify-center items-center w-full landscape:w-1/3 h-full">
          <QRCode isLargeScreen={isLargeScreen} />
        </div>
      </div>
    </Box>
  );
}

export default LiveTeamboardPage;
